import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import Layout        from './components/layout/Layout';
import { FilterProvider } from './components/layout/FilterContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import IpiCalculator from './components/IpiCalculator';
import { playbookRole, MEDICAL_ADVISOR } from './components/playbook/playbookRole';
import AuthRoute      from './components/common/AuthRoute';
import IdleWarningModal from './components/common/IdleWarningModal';

import RWEDashboard    from './components/RWEDashboard';
import Glossary        from './components/Glossary';
import Login           from './components/Login';
import ForgotPassword  from './components/ForgotPassword';
import ResetPassword   from './components/ResetPassword';
import UserManagement  from './components/UserManagement';
import AuditLogs       from './components/AuditLogs';
import EngagementReport from './components/EngagementReport';
import TermsModal      from './components/TermsModal';
import PatientMilestones from './components/PatientMilestones';
import EmailCampaigns    from './components/EmailCampaigns';

import { getAuthData, clearAuthData } from './utils/auth';
import { logout as apiLogout, refreshAccessToken, acceptTerms as apiAcceptTerms } from './services/authService';
import { useIdleTimer } from './hooks/useIdleTimer';
import { useSessionHeartbeat } from './hooks/useSessionHeartbeat';

// CAR-T Playbook — lazy-loaded / code-split (ACT-371)
const CartPlaybook = lazy(() => import('./components/playbook/CartPlaybook'));

const App = () => {
  const [user, setUser]                         = useState(null);
  const [isAuthenticated, setIsAuthenticated]   = useState(false);
  const [isLoading, setIsLoading]               = useState(true);
  const [showTermsModal, setShowTermsModal]     = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [isDecliningTerms, setIsDecliningTerms] = useState(false);
  const sessionRestored                         = useRef(false);

  // Restore session on app load
  useEffect(() => {
    // Prevent StrictMode double-invocation from firing two refresh requests
    if (sessionRestored.current) return;
    sessionRestored.current = true;

    const restoreSession = async () => {
      try {
        const { isValid, user: storedUser, needsRefresh } = getAuthData();

        if (isValid && storedUser) {
          setUser(storedUser);
          setIsAuthenticated(true);
          // If session restored but T&C not yet accepted, show modal
          if (!storedUser.terms_accepted) {
            setShowTermsModal(true);
          }
          return;
        }

        if (needsRefresh && storedUser) {
          const refreshedUser = await refreshAccessToken();
          if (refreshedUser) {
            setUser(refreshedUser);
            setIsAuthenticated(true);
            if (!refreshedUser.terms_accepted) {
              setShowTermsModal(true);
            }
            return;
          }
        }

        clearAuthData();
      } catch {
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const handleLogin = (userData) => {
    // Tokens already stored by authService.login()
    // Update React state, then check if T&C needs to be accepted
    setUser(userData);
    setIsAuthenticated(true);
    if (!userData.terms_accepted) {
      setShowTermsModal(true);
    }
  };

  const handleAcceptTerms = async () => {
    setIsAcceptingTerms(true);
    try {
      await apiAcceptTerms();
      // Update local user state so the modal doesn't reappear
      const updatedUser = { ...user, terms_accepted: true, terms_accepted_at: new Date().toISOString() };
      setUser(updatedUser);
      // Only update the stored user object — leave tokens untouched
      localStorage.setItem('act_user', JSON.stringify(updatedUser));
      setShowTermsModal(false);
    } catch (err) {
      console.error('Failed to accept terms:', err);
      // Don't block the user — close modal and let them proceed
      // The modal will reappear on next login until accepted successfully
      setShowTermsModal(false);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  const handleDeclineTerms = async () => {
    setIsDecliningTerms(true);
    try {
      await apiLogout(); // revoke tokens on server + clear localStorage
    } catch {
      // Even if server call fails, clear local state
    } finally {
      setIsDecliningTerms(false);
      setShowTermsModal(false);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    await apiLogout('manual');
    setUser(null);
    setIsAuthenticated(false);
    setShowTermsModal(false);
  };

  const handleIdleLogout = async () => {
    await apiLogout('idle_timeout');
    setUser(null);
    setIsAuthenticated(false);
    setShowTermsModal(false);
  };

  // Auto-logout after 30 minutes of inactivity
  const { showWarning, remainingSeconds, stayActive } = useIdleTimer({
    timeout: 30 * 60 * 1000, // 30 minutes
    warningDuration: 60 * 1000, // 1 minute warning
    onLogout: handleIdleLogout,
    enabled: isAuthenticated,
  });

  // Session heartbeat — keeps engagement-report time accurate for sessions that
  // end by tab-close instead of an explicit logout.
  useSessionHeartbeat(isAuthenticated);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading ACTelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontSize: '14px' },
          success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
          error: { duration: 5000 },
        }}
      />
      <Router>
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <Login onLogin={handleLogin} />
            </AuthRoute>
          } />
          <Route path="/forgot-password" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <ForgotPassword />
            </AuthRoute>
          } />
          <Route path="/reset-password" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <ResetPassword />
            </AuthRoute>
          } />

          {/* Protected application routes */}
          <Route path="/" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <FilterProvider>
                <Layout user={user} onLogout={handleLogout} />
              </FilterProvider>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<RWEDashboard />} />
            <Route path="glossary" element={<Glossary />} />

            {/* Superadmin only — stakeholder cannot access */}
            <Route path="user-management" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="audit-logs" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <AuditLogs />
              </ProtectedRoute>
            } />
            <Route path="engagement-report" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <EngagementReport />
              </ProtectedRoute>
            } />
            <Route path="patient-milestones" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['superadmin', 'stakeholder']} userRole={user?.role}>
                <PatientMilestones />
              </ProtectedRoute>
            } />
            <Route path="email-campaigns" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <EmailCampaigns />
              </ProtectedRoute>
            } />

            {/* IPI Calculator — Sales users (and superadmin); no patient data (feedback #13) */}
            <Route path="ipi-calculator" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['sales', 'superadmin']} userRole={user?.role}>
                <IpiCalculator />
              </ProtectedRoute>
            } />

            {/* CAR-T Playbook — physician (CRUD) + superadmin/stakeholder (read-only) */}
            <Route path="playbook" element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                requiredRoles={['physician', 'superadmin', 'stakeholder', MEDICAL_ADVISOR]}
                userRole={playbookRole(user)}
              >
                <Suspense fallback={
                  <div className="p-6 text-sm text-gray-500">Loading CAR-T Playbook…</div>
                }>
                  <CartPlaybook />
                </Suspense>
              </ProtectedRoute>
            } />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        {/* Blocking T&C acceptance modal — shown after first login */}
        <TermsModal
          isOpen={showTermsModal}
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
          mode="accept"
          isAccepting={isAcceptingTerms}
          isDeclining={isDecliningTerms}
        />

        {/* Idle timeout warning — auto-logout after 30 min inactivity */}
        <IdleWarningModal
          isOpen={showWarning}
          remainingSeconds={remainingSeconds}
          onStayActive={stayActive}
          onLogout={handleLogout}
        />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
